import { useT } from "../../../shared/i18n/i18n";
import { ChevronDown, Zap } from "lucide-react";
import { sprintStateFor, todayIso } from "../../../entities/sprint-planning/model/use-sprints";
import type { Sprint } from "../../../entities/sprint-planning/model/use-sprints";
import { cn } from "../../../shared/lib/cn";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { sprintStateMeta } from "./sprint-state";

const allScope = "__all__";

// Scopes every regular view of a sprint board: a concrete sprint, the
// backlog, or the whole track.
export function SprintScopeSwitcher({
  onChange,
  sprints,
  value,
}: {
  onChange: (value: string | null) => void;
  sprints: Sprint[];
  value: string | null;
}) {
  const t = useT();
  const today = todayIso();
  const selectedSprint = sprints.find((sprint) => sprint.id === value) ?? null;
  const label = value === null ? t("sprint.allItems") : value === "backlog" ? t("sprint.backlog") : selectedSprint?.name ?? "Sprint";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button size="sm" variant="outline">
            <Zap data-icon="inline-start" />
            <span className="max-w-36 truncate">{label}</span>
            {selectedSprint ? (
              <span className={cn("size-1.5 rounded-full", sprintStateMeta[sprintStateFor(selectedSprint, today)].dotClassName)} />
            ) : null}
            <ChevronDown data-icon="inline-end" className="text-muted-foreground" />
          </Button>
        }
      />
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuRadioGroup
          onValueChange={(next) => onChange(next === allScope ? null : String(next))}
          value={value ?? allScope}
        >
          {sprints.map((sprint) => {
            const state = sprintStateFor(sprint, today);
            return (
              <DropdownMenuRadioItem key={sprint.id} value={sprint.id}>
                <span className={cn("size-1.5 shrink-0 rounded-full", sprintStateMeta[state].dotClassName)} />
                <span className="min-w-0 flex-1 truncate">{sprint.name}</span>
                <span className="text-xs text-muted-foreground">{t(sprintStateMeta[state].labelKey)}</span>
              </DropdownMenuRadioItem>
            );
          })}
          <DropdownMenuRadioItem value="backlog">
            <span className="size-1.5 shrink-0 rounded-full bg-muted-foreground/40" />
            {t("sprint.backlog")}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value={allScope}>
            <span className="size-1.5 shrink-0 rounded-full bg-muted-foreground/40" />
            {t("sprint.allItems")}
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
